/**
 * SwapModal Component
 * Modal for swapping between WiTon and USDT (both directions)
 */

import { useState } from 'react';
import { api } from '../services/api';

interface SwapModalProps {
  isOpen: boolean;
  onClose: () => void;
  witonBalance: number;
  usdtBalance: number;
  onSwapSuccess: () => void;
}

export function SwapModal({ isOpen, onClose, witonBalance, usdtBalance, onSwapSuccess }: SwapModalProps) {
  const [amount, setAmount] = useState('');
  const [direction, setDirection] = useState<'witon-to-usdt' | 'usdt-to-witon'>('witon-to-usdt');
  const EXCHANGE_RATE = 5000;
  const [isLoading, setIsLoading] = useState(false);

  const getFees = () => {
    if (direction === 'witon-to-usdt') {
      return { feePercent: 8, burningFee: 55_000, minWiton: 250_000, maxWiton: 500_000 };
    }
    return { feePercent: 20, burningFee: 5_500 };
  };

  const handleSwap = async () => {
    const numAmount = parseFloat(amount);
    if (isNaN(numAmount) || numAmount <= 0) { alert('Enter a valid amount'); return; }
    const fees = getFees();

    if (direction === 'witon-to-usdt') {
      if (numAmount < fees.minWiton!) { alert(`Minimum: ${fees.minWiton!.toLocaleString()} WiTon`); return; }
      if (numAmount > fees.maxWiton!) { alert(`Maximum: ${fees.maxWiton!.toLocaleString()} WiTon per swap`); return; }
      if (numAmount + fees.burningFee > witonBalance) { alert('Insufficient WiTon balance'); return; }
    } else {
      if (numAmount > usdtBalance) { alert('Insufficient USDT balance'); return; }
      if (witonBalance < fees.burningFee) { alert(`Insufficient WiTon for Burning Fee (${fees.burningFee.toLocaleString()} WiTon)`); return; }
    }

    setIsLoading(true);
    try {
      if (direction === 'witon-to-usdt') {
        await api.swapWitonToUsdt(numAmount);
      } else {
        await api.swapUsdtToWiton(numAmount);
      }
      alert('Swap successful!');
      onSwapSuccess();
      onClose();
      setAmount('');
    } catch (error: any) {
      alert(error.response?.data?.message || 'Swap failed');
    } finally {
      setIsLoading(false);
    }
  };

  const calcOutput = () => {
    const n = parseFloat(amount);
    if (isNaN(n) || n <= 0) return { output: 0, fee: 0 };
    const fees = getFees();
    if (direction === 'witon-to-usdt') {
      const gross = n / EXCHANGE_RATE;
      const fee = gross * (fees.feePercent / 100);
      return { output: gross - fee, fee };
    } else {
      const fee = n * (fees.feePercent / 100);
      const net = n - fee;
      return { output: net * EXCHANGE_RATE, fee };
    }
  };

  const fees = getFees();
  const { output, fee } = calcOutput();
  const fromLabel = direction === 'witon-to-usdt' ? 'WiTon' : 'USDT';
  const toLabel = direction === 'witon-to-usdt' ? 'USDT' : 'WiTon';
  const fromBalance = direction === 'witon-to-usdt' ? witonBalance : usdtBalance;

  if (!isOpen) return null;

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' }}>
      <div style={{ background: 'linear-gradient(180deg, #1a2332 0%, #0a0e1a 100%)', borderRadius: '20px', padding: '24px', maxWidth: '400px', width: '100%', border: '1px solid rgba(59, 130, 246, 0.3)' }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 700 }}>Swap</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#fff', fontSize: '24px', cursor: 'pointer', padding: 0 }}>×</button>
        </div>

        {/* Direction Toggle */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '16px' }}>
          <button onClick={() => { setDirection('witon-to-usdt'); setAmount(''); }}
            style={{ padding: '10px 6px', borderRadius: '10px', border: 'none', cursor: 'pointer', fontSize: '13px', fontWeight: 600, color: '#fff', whiteSpace: 'nowrap', overflow: 'visible',
              background: direction === 'witon-to-usdt' ? 'linear-gradient(135deg, #7b1fa2 0%, #9c27b0 100%)' : 'rgba(255,255,255,0.1)' }}>
            WiTon → USDT
          </button>
          <button onClick={() => { setDirection('usdt-to-witon'); setAmount(''); }}
            style={{ padding: '10px 6px', borderRadius: '10px', border: 'none', cursor: 'pointer', fontSize: '13px', fontWeight: 600, color: '#fff', whiteSpace: 'nowrap', overflow: 'visible',
              background: direction === 'usdt-to-witon' ? 'linear-gradient(135deg, #1b5e20 0%, #2e7d32 100%)' : 'rgba(255,255,255,0.1)' }}>
            USDT → WiTon
          </button>
        </div>

        {/* From */}
        <div style={{ background: 'rgba(10, 22, 40, 0.6)', borderRadius: '12px', padding: '14px', marginBottom: '10px' }}>
          <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.6)', marginBottom: '6px' }}>
            From {direction === 'witon-to-usdt' ? `(min ${(fees as any).minWiton?.toLocaleString()} — max ${(fees as any).maxWiton?.toLocaleString()} WiTon)` : '(USDT)'}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0"
              style={{ flex: 1, background: 'none', border: 'none', color: '#fff', fontSize: '22px', fontWeight: 600, outline: 'none' }} />
            <div style={{ fontSize: '14px', fontWeight: 600, color: direction === 'witon-to-usdt' ? '#9c27b0' : '#2e7d32', whiteSpace: 'nowrap', flexShrink: 0 }}>{fromLabel}</div>
          </div>
          <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)', marginTop: '4px' }}>
            Balance: {fromBalance.toLocaleString('en-US', { maximumFractionDigits: direction === 'usdt-to-witon' ? 2 : 0 })}
          </div>
        </div>

        {/* Arrow */}
        <div style={{ textAlign: 'center', fontSize: '20px', margin: '4px 0' }}>⇅</div>

        {/* To */}
        <div style={{ background: 'rgba(10, 22, 40, 0.6)', borderRadius: '12px', padding: '14px', marginBottom: '12px' }}>
          <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.6)', marginBottom: '6px' }}>To (after {fees.feePercent}% Funding Fee)</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{ flex: 1, fontSize: '22px', fontWeight: 600, color: '#10b981' }}>
              {direction === 'witon-to-usdt' ? output.toFixed(2) : Math.floor(output).toLocaleString()}
            </div>
            <div style={{ fontSize: '14px', fontWeight: 600, color: direction === 'witon-to-usdt' ? '#2e7d32' : '#9c27b0', whiteSpace: 'nowrap', flexShrink: 0 }}>{toLabel}</div>
          </div>
          <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.4)', marginTop: '4px' }}>
            Rate: 1 USDT = {EXCHANGE_RATE.toLocaleString()} WiTon • Funding Fee: {fee.toFixed(2)} USDT ({fees.feePercent}%) • Burning Fee: {fees.burningFee.toLocaleString()} WiTon
          </div>
        </div>

        {/* Swap Button */}
        <button onClick={handleSwap} disabled={isLoading || !amount || parseFloat(amount) <= 0}
          style={{ width: '100%', padding: '14px', background: isLoading || !amount ? 'rgba(59,130,246,0.3)' : 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
            border: 'none', borderRadius: '12px', color: '#fff', fontSize: '16px', fontWeight: 700,
            cursor: isLoading || !amount ? 'not-allowed' : 'pointer',
            boxShadow: isLoading || !amount ? 'none' : '0 4px 20px rgba(16, 185, 129, 0.4)' }}>
          {isLoading ? 'Swapping...' : 'Swap'}
        </button>
      </div>
    </div>
  );
}
